import { type FC, useLayoutEffect } from "react";
import { useDebug, usePubSub } from "@huub-web/ui";
import {
  WebSocketProvider as BaseWebSocketProvider,
  type WebSocketProviderProps as BaseWebSocketProviderProps,
} from "@huub-web/backend";

import { useAppSettings } from "../../hooks/useAppSettings";

type WebSocketProviderProps = Omit<BaseWebSocketProviderProps, "debug"> & {
  ttsEnabled: boolean;
};

/**
 * WebSocketProvider component that provides a WebSocket connection to the application and handles the connection state.
 */
export const WebSocketProvider: FC<WebSocketProviderProps> = ({
  ttsEnabled = false,
  children,
  ...props
}) => {
  const { isAudioEnabled, isVisualDebugEnabled, toggleVisualDebug } =
    useAppSettings();
  const pubSub = usePubSub();
  const { debug } = useDebug();

  useLayoutEffect(() => {
    if (window === undefined) {
      return;
    }
    if (window?.location?.search?.includes("debug")) {
      if (!isVisualDebugEnabled) {
        toggleVisualDebug();
      }
    }
  }, [location, isVisualDebugEnabled, toggleVisualDebug]);

  const handleFailure = () => {
    pubSub?.publish("snackbar", {
      body: "De AI hypotheekadviseur is tijdelijk niet beschikbaar.",
      variant: "error",
    });
  };

  return (
    <BaseWebSocketProvider
      {...props}
      params={{ tts_enabled: ttsEnabled && isAudioEnabled ? "true" : "false" }}
      debug={debug && isVisualDebugEnabled}
      onFailure={handleFailure}
    >
      {children}
    </BaseWebSocketProvider>
  );
};
